import { useEffect, useState, useRef } from 'react';
import { Result, Spin, Alert } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { TextService } from '../../Services/TextService';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import LeftMenuCommunicate from './LeftCommunicate';
import { BatchJobService } from '../../Services/BatchJobService';
import { JobType } from '../../Types/JobType';
import { BatchStatus } from '../../Types/BatchStatus';
import { SyncOutlined } from '@ant-design/icons';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';

const AdminBatchJobStatus = () => {
    const { id } = useParams<{ id: string }>();
    const [job, setJob] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const [pollInterval, setPollInterval] = useState(2000);
    const navigate = useNavigate();
    const GetText = TextService.GetText;
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const maxRetries = 5;

    useEffect(() => {
        if (!id) {
            setError(GetText("BatchJob_InvalidId"));
            setIsLoading(false);
            return;
        }

        // Create abort controller for cleanup
        abortControllerRef.current = new AbortController();

        loadJobStatus();

        // Poll with dynamic interval (exponential backoff on errors)
        intervalRef.current = setInterval(() => {
            loadJobStatus();
        }, pollInterval);

        return () => {
            // Cleanup: clear interval and abort any pending requests
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, [id, pollInterval]);

    useEffect(() => {
        // Stop polling when job is completed or failed
        if (job && (job.status === BatchStatus.Completed || job.status === BatchStatus.Failed)) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [job?.status]);

    const loadJobStatus = async () => {
        // Circuit breaker: stop polling after max retries
        if (retryCount >= maxRetries) {
            setError(GetText("BatchJob_ErrorLoading"));
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        try {
            const jobData = await BatchJobService.GetBatchJob(parseInt(id!));
            setJob(jobData);
            setIsLoading(false);

            // Reset retry count on success
            if (retryCount > 0) {
                setRetryCount(0);
                setPollInterval(2000); // Reset to normal polling
            }
        } catch (err) {
            // Don't show error if request was aborted (component unmounted)
            if (err instanceof Error && err.name === 'AbortError') {
                return;
            }

            // Increment retry count and apply exponential backoff
            const newRetryCount = retryCount + 1;
            setRetryCount(newRetryCount);

            // Exponential backoff: 2s, 4s, 8s, 16s, 30s (max)
            const newInterval = Math.min(2000 * Math.pow(2, newRetryCount), 30000);
            setPollInterval(newInterval);

            // Only show error after multiple failures
            if (newRetryCount >= 3) {
                setError(GetText("BatchJob_ErrorLoading"));
            }
            setIsLoading(false);
        }
    };

    const getJobTitle = (jobType: number, status: number) => {
        // Show processing title if not completed
        if (status === BatchStatus.Processing || status === BatchStatus.Pending) {
            switch (jobType) {
                case JobType.SendEmail:
                    return GetText("BatchJob_EmailSendingTitle");
                case JobType.SendSms:
                    return GetText("BatchJob_SmsSendingTitle");
                case JobType.CreatePaymentLinks:
                    return GetText("BatchJob_PaymentLinksCreatingTitle");
                case JobType.CreateFortnoxInvoices:
                    return GetText("BatchJob_FortnoxInvoicesCreatingTitle");
                case JobType.CreateFortnoxCustomers:
                    return GetText("BatchJob_FortnoxCustomersCreatingTitle");
                default:
                    return GetText("BatchJob_JobProcessingTitle");
            }
        }

        // Show completed title
        switch (jobType) {
            case JobType.SendEmail:
                return GetText("BatchJob_EmailSentTitle");
            case JobType.SendSms:
                return GetText("BatchJob_SmsSentTitle");
            case JobType.CreatePaymentLinks:
                return GetText("BatchJob_PaymentLinksSentTitle");
            case JobType.CreateFortnoxInvoices:
                return GetText("BatchJob_FortnoxInvoicesSentTitle");
            case JobType.CreateFortnoxCustomers:
                return GetText("BatchJob_FortnoxCustomersSentTitle");
            default:
                return GetText("BatchJob_JobCreatedTitle");
        }
    };

    const getJobSubTitle = (jobType: number, totalItems: number, completedItems: number, status: number) => {
        // Show progress if processing
        if (status === BatchStatus.Processing) {
            return GetText("BatchJob_ProcessingDescription")
                .replace("{completed}", completedItems.toString())
                .replace("{total}", totalItems.toString());
        }

        // Show pending message
        if (status === BatchStatus.Pending) {
            return GetText("BatchJob_PendingDescription").replace("{count}", totalItems.toString());
        }

        // Show completed message
        switch (jobType) {
            case JobType.SendEmail:
                return GetText("BatchJob_EmailSentDescription").replace("{count}", totalItems.toString());
            case JobType.SendSms:
                return GetText("BatchJob_SmsSentDescription").replace("{count}", totalItems.toString());
            case JobType.CreatePaymentLinks:
                return GetText("BatchJob_PaymentLinksSentDescription").replace("{count}", totalItems.toString());
            case JobType.CreateFortnoxInvoices:
                return GetText("BatchJob_FortnoxInvoicesSentDescription").replace("{count}", totalItems.toString());
            case JobType.CreateFortnoxCustomers:
                return GetText("BatchJob_FortnoxCustomersSentDescription").replace("{count}", totalItems.toString());
            default:
                return GetText("BatchJob_JobCreatedDescription").replace("{count}", totalItems.toString());
        }
    };

    const getResultStatus = (status: number) => {
        if (status === BatchStatus.Failed) return 'error';
        if (status === BatchStatus.Completed) return 'success';
        return 'info';
    };

    const getResultIcon = (status: number) => {
        if (status === BatchStatus.Processing || status === BatchStatus.Pending) {
            return <SyncOutlined spin style={{ fontSize: 72, color: '#1890ff' }} />;
        }
        return undefined; // Use default icon
    };

    if (isLoading) {
        return (
            <LayoutLeftMenu menu={<LeftMenuCommunicate selected={"batchJobHistory"} />}>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" />
                </div>
            </LayoutLeftMenu>
        );
    }

    if (error || !job) {
        return (
            <LayoutLeftMenu menu={<LeftMenuCommunicate selected={"batchJobHistory"} />}>
                <Alert
                    message={GetText("Common_Error")}
                    description={
                        retryCount > 0
                            ? `${error || GetText("BatchJob_NotFound")} (Retry ${retryCount}/${maxRetries})`
                            : error || GetText("BatchJob_NotFound")
                    }
                    type="error"
                    showIcon
                />
            </LayoutLeftMenu>
        );
    }

    return (
        <LayoutLeftMenu menu={<LeftMenuCommunicate selected={"batchJobHistory"} />}>
            <Result
                status={getResultStatus(job.status)}
                icon={getResultIcon(job.status)}
                title={getJobTitle(job.jobType, job.status)}
                subTitle={getJobSubTitle(job.jobType, job.totalItems, job.completedItems || 0, job.status)}
            />
        </LayoutLeftMenu>
    );
};

export default AdminBatchJobStatus;
